const CHOICE_KEY = "believe.analytics.consent.v1"
const SIX_MONTHS = 180 * 24 * 60 * 60 * 1000
const FORMS = new Set(["home_contact", "engineering_challenges"])

export function safeUrl(value, base) {
  if (!value) return ""
  try {
    const url = new URL(value, base)
    if (!["https:", "http:"].includes(url.protocol)) return ""
    // The site's public routes contain no personal identifiers. Drop queries and fragments.
    return url.origin + url.pathname
  } catch { return "" }
}

export function installAnalytics(win, doc) {
  const id = doc.querySelector('meta[name="google-analytics-id"]')?.content
  if (!/^G-[A-Z0-9]+$/.test(id || "")) return
  const read = (storage, key) => { try { return JSON.parse(storage.getItem(key)) } catch { return null } }
  const write = (storage, key, value) => { try { storage.setItem(key, JSON.stringify(value)) } catch { /* Storage may be unavailable. */ } }
  let choice = null
  try {
    const saved = read(win.localStorage, CHOICE_KEY)
    if (saved?.expires > Date.now() && ["granted", "denied"].includes(saved.value)) choice = saved.value
  } catch { /* No persistent storage. */ }
  let started = false
  let pageSent = false
  let scrolled = false
  let previousPage = safeUrl(doc.referrer, win.location.href)
  let lastPage = null
  let lastVisitKey = null
  let startedForms = new WeakSet()
  const seenLeads = new Set()
  win.dataLayer = win.dataLayer || []
  win.gtag = win.gtag || function () { win.dataLayer.push(arguments) }
  win[`ga-disable-${id}`] = choice !== "granted"

  function context() {
    return {
      page_location: safeUrl(win.location.href),
      page_referrer: previousPage,
      page_title: doc.title,
      language: doc.documentElement.lang === "fr" ? "fr" : "en"
    }
  }

  function start() {
    if (started || choice !== "granted") return
    started = true
    win[`ga-disable-${id}`] = false
    win.gtag("consent", "default", { analytics_storage: "granted", ad_storage: "denied", ad_user_data: "denied", ad_personalization: "denied" })
    win.gtag("js", new Date())
    win.gtag("config", id, {
      ...context(), send_page_view: false, allow_google_signals: false,
      allow_ad_personalization_signals: false, cookie_expires: 15552000
    })
    const script = doc.createElement("script")
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`
    doc.head.appendChild(script)
  }

  function event(name, parameters = {}) {
    if (choice !== "granted") return
    start()
    win.gtag("event", name, { ...context(), ...parameters, send_to: id })
  }

  function pageView() {
    if (pageSent || choice !== "granted") return
    pageSent = true
    start()
    win.gtag("set", context())
    event("page_view")
  }

  function successes() {
    doc.querySelectorAll("[data-analytics-success]").forEach(marker => {
      const token = marker.dataset.analyticsSuccess
      const form = marker.dataset.analyticsForm
      marker.remove()
      if (!token || !FORMS.has(form)) return
      let stored = []
      try { stored = read(win.sessionStorage, "believe.analytics.leads") || [] } catch { /* No storage. */ }
      if (!Array.isArray(stored)) stored = []
      if (seenLeads.has(token) || stored.includes(token)) return
      seenLeads.add(token)
      try { write(win.sessionStorage, "believe.analytics.leads", [...stored, token].slice(-100)) } catch { /* No storage. */ }
      // A submission without consent is discarded, never replayed after later acceptance.
      event("generate_lead", { form_id: form })
    })
  }

  function showChoice() {
    const banner = doc.querySelector("[data-analytics-consent]")
    if (banner) banner.hidden = choice !== null
  }

  function clearCookies() {
    const parts = win.location.hostname.split(".")
    const domains = ["", ...parts.map((_, i) => `; domain=.${parts.slice(i).join(".")}`)]
    doc.cookie.split(";").forEach(cookie => {
      const name = cookie.split("=")[0].trim()
      if (name === "_ga" || name.startsWith("_ga_")) {
        domains.forEach(domain => { doc.cookie = `${name}=; Max-Age=0; path=/${domain}` })
      }
    })
  }

  function choose(value) {
    choice = value
    try { write(win.localStorage, CHOICE_KEY, { value, expires: Date.now() + SIX_MONTHS }) } catch { /* No storage. */ }
    showChoice()
    if (value === "granted") {
      win[`ga-disable-${id}`] = false
      if (started) win.gtag("consent", "update", { analytics_storage: "granted" })
      pageView()
    } else {
      win[`ga-disable-${id}`] = true
      clearCookies()
      // Reload removes the previously loaded tag and its automatic engagement listeners.
      if (started) win.location.reload()
    }
  }

  doc.addEventListener("click", e => {
    const button = e.target.closest("[data-analytics-choice]")
    if (button) { choose(button.dataset.analyticsChoice); return }
    if (e.target.closest("[data-analytics-settings]")) {
      const banner = doc.querySelector("[data-analytics-consent]")
      if (banner) { banner.hidden = false; banner.querySelector("button")?.focus() }
      return
    }
    const link = e.target.closest("a[data-analytics-click]")
    if (link) event("cta_click", { link_id: link.dataset.analyticsClick, link_url: safeUrl(link.href, win.location.href) })
  })
  doc.addEventListener("focusin", e => {
    const form = e.target.closest("form[data-analytics-form]")
    if (!form || startedForms.has(form) || choice !== "granted") return
    startedForms.add(form)
    event("form_start", { form_id: form.dataset.analyticsForm })
  })
  win.addEventListener("scroll", () => {
    const height = doc.documentElement.scrollHeight
    if (!scrolled && choice === "granted" && height > win.innerHeight && win.scrollY + win.innerHeight >= height * .9) {
      scrolled = true
      event("scroll", { percent_scrolled: 90 })
    }
  }, { passive: true })
  win.addEventListener("storage", e => {
    if (e.key === CHOICE_KEY) win.location.reload()
  })
  doc.addEventListener("turbo:before-cache", () => {
    doc.querySelectorAll("[data-analytics-success]").forEach(marker => marker.remove())
  })
  doc.addEventListener("turbo:load", () => {
    const url = new URL(win.location.href)
    const visitKey = url.origin + url.pathname + url.search
    if (visitKey === lastVisitKey) { showChoice(); successes(); return }
    lastVisitKey = visitKey
    if (lastPage) previousPage = lastPage
    lastPage = safeUrl(win.location.href)
    pageSent = false
    scrolled = false
    startedForms = new WeakSet()
    showChoice()
    pageView()
    successes()
  })
  new win.MutationObserver(successes).observe(doc.body, { childList: true, subtree: true })
  showChoice()
}
